import React, { forwardRef, useMemo, useState, useEffect } from 'react';
import { useCoordination } from '@atlassiansox/engagekit-ts';
import { SpotlightTransition } from '@atlaskit/onboarding';
import { Flex, xcss } from '@atlaskit/primitives';
import { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { Environment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import spotlightImage from './assets/onboarding.svg';
import messages from './messages';

/* TODO: remove after cleaning list_view_conditional_formatting and all the Spotlight targets  */
export const FORMATTING_RULES_ONBOARDING_ENGAGEMENT_ID_STG = 'STG-EXT-1441';
export const FORMATTING_RULES_ONBOARDING_ENGAGEMENT_ID_PROD =
	'jira-list-view-format-rules-onboarding';

const ENV_STAGING: Environment = 'staging';
const STARGATE_URL = '/gateway/api';

export const Onboarding = forwardRef(
	(
		{ onClickFormatButton }: { onClickFormatButton: (x: { showOnboarding: boolean }) => void },
		ref: React.Ref<HTMLElement>,
	) => {
		const ONBOARDING_ENGAGEMENT_ID =
			useEnvironment() === ENV_STAGING
				? FORMATTING_RULES_ONBOARDING_ENGAGEMENT_ID_STG
				: FORMATTING_RULES_ONBOARDING_ENGAGEMENT_ID_PROD;

		const cloudId = useCloudId();
		/**
		 * Onboarding Experience is loaded manually on page load via EP's CoordinationClient class
		 * Once loaded, if the message is active for this particular context, only then the FORMATTING_RULES_ONBOARDING will be loaded and displayed
		 */
		const coordinationClient = useMemo(
			() => new CoordinationClient(cloudId, STARGATE_URL),
			[cloudId],
		);

		const [isEnrolled, stopEnrolment] = useCoordination(
			coordinationClient,
			ONBOARDING_ENGAGEMENT_ID,
		);

		const [isSpotlightActive, setIsSpotlightActive] = useState(true);
		const { formatMessage } = useIntl();

		useEffect(() => {
			if (isEnrolled) {
				stopEnrolment(false);
			}
		}, [isEnrolled, stopEnrolment]);

		return (
			<SpotlightTransition>
				{isEnrolled && isSpotlightActive && (
					<JiraSpotlight
						messageId="business-conditional-formatting-sidebar.ui.format-rules-sidebar.sidebar.onboarding.jira-spotlight.format-rule-button"
						messageType="engagement"
						image={spotlightImage}
						actionsBeforeElement="1/3"
						actions={[
							{
								onClick: () => {
									setIsSpotlightActive(false);
									onClickFormatButton({ showOnboarding: true });
								},
								text: formatMessage(messages.next),
							},
							{
								appearance: 'subtle',
								onClick: () => setIsSpotlightActive(false),
								text: formatMessage(messages.dismiss),
							},
						]}
						dialogWidth={320}
						heading={formatMessage(messages.onboardingHeading)}
						target="format-rule-button"
						key="format-rule-button"
						targetRadius={3}
						footer={({ children }) => (
							<Flex
								ref={ref}
								alignItems="center"
								justifyContent="space-between"
								xcss={footerContainerStyle}
							>
								{children}
							</Flex>
						)}
					>
						{formatMessage(messages.onboardingDescription)}
					</JiraSpotlight>
				)}
			</SpotlightTransition>
		);
	},
);

export const SidebarOnboarding = ({ showOnboarding }: { showOnboarding: boolean }) => {
	const { formatMessage } = useIntl();
	const [activeSpotlight, setActiveSpotlight] = useState<null | number>(null);
	const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);
	const end = () => setActiveSpotlight(null);

	useEffect(() => {
		if (showOnboarding) {
			setTimeout(() => {
				setActiveSpotlight(0);
			}, 100);
		}
	}, [showOnboarding]);

	const renderActiveSpotlight = () => {
		const spotlights = [
			<JiraSpotlight
				messageId="business-conditional-formatting-sidebar.ui.format-rules-sidebar.sidebar.onboarding.jira-spotlight.create-rule-form"
				messageType="engagement"
				actionsBeforeElement="2/3"
				actions={[
					{
						onClick: next,
						text: 'Next',
					},
					{
						appearance: 'subtle',
						onClick: end,
						text: formatMessage(messages.dismiss),
					},
				]}
				dialogWidth={280}
				target="create-rule-form"
				key="create-rule-form"
				targetRadius={3}
			>
				{formatMessage(messages.createRuleOnboarding)}
			</JiraSpotlight>,
			<JiraSpotlight
				messageId="business-conditional-formatting-sidebar.ui.format-rules-sidebar.sidebar.onboarding.jira-spotlight.drag-rule-form"
				messageType="engagement"
				actionsBeforeElement="3/3"
				actions={[
					{
						onClick: end,
						text: formatMessage(messages.done),
					},
				]}
				dialogWidth={280}
				target="drag-rule-form"
				key="drag-rule-form"
				targetRadius={3}
			>
				{formatMessage(messages.rulesOrderOnboarding)}
			</JiraSpotlight>,
		];

		if (activeSpotlight === null) {
			return null;
		}

		return spotlights[activeSpotlight];
	};

	return <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>;
};

const footerContainerStyle = xcss({
	paddingTop: 'space.050',
});
